import type { Macro } from '@/components/MacroPicker'

// User action URLs for admin shortcuts
export interface UserActions {
  impersonate?: string
  admin?: string
}

export interface CompanyActions {
  impersonate_owner?: string
  set_pending_payment?: string
  increase_trial_5_days?: string
  increase_trial_7_days?: string
  remove_read_only?: string
}

export interface CompanyStatus {
  status?: 'trial' | 'active' | 'read_mode' | 'trial_expired'
  plan?: string
  payment_method?: 'credit_card' | 'boleto'
  trial_days_left?: number
  establishment_id?: number
  user_id?: number
  payment_gateway?: 'asaas' | 'stripe'
  registered_at?: string
  patients_count?: number
}

export interface CustomerJourney {
  opportunity: boolean
  did_presentation: boolean
  signed_up: boolean
  paying_customer: boolean
  engaged: boolean
}

export interface EndUser {
  id: number
  name: string
  email: string | null
  company: string | null
  company_id: number | null
  phone: string | null
  locale: string | null
  location: string | null
  first_seen_at: string | null
  last_seen_at: string | null
  signed_up_at: string | null
  web_sessions_count: number
  browser: string | null
  os: string | null
  browser_language: string | null
  last_contacted_at: string | null
  last_heard_from_at: string | null
  messages_count: number
  conversations_count: number
  current_page_url: string | null
  current_page_title: string | null
  recent_page_views: PageView[]
  recent_conversations: RecentConversation[]
  contact_notes: ContactNote[]
  user_actions?: UserActions
  company_actions?: CompanyActions
  company_status?: CompanyStatus | null
  custom_attributes?: Record<string, unknown> | null
}

export interface PageView {
  id: number
  url: string
  title: string | null
  created_at: string
}

export interface ContactNote {
  id: number
  body: string
  created_at: string
  agent: Agent | null
}

export interface RecentConversation {
  id: number
  status: string
  created_at: string
  last_message_at: string
  assigned_agent: {
    id: number
    name: string
    avatar_url: string | null
  } | null
}

export interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
  online: boolean
}

export interface TagType {
  id: number
  name: string
  color: string
}

// Lightweight end user for conversation list (minimal fields)
export interface EndUserListItem {
  id: number
  name: string
  email: string | null
  company: string | null
  last_seen_at: string | null
}

export interface ConversationListItem {
  id: number
  status: string
  priority: string
  source: string | null
  end_user: EndUserListItem
  assigned_agent: Agent | null
  tags: TagType[]
  last_message: string | null
  last_message_is_note?: boolean
  last_message_at: string
  unread_count: number
  has_new_end_user_message?: boolean
  team_id: number | null
}

export interface Team {
  id: number
  name: string
  emoji: string | null
  channels: string[]
  open_count: number
}

export interface ConversationDetail {
  id: number
  status: string
  priority: string
  source: string | null
  end_user: EndUser
  assigned_agent: Agent | null
  tags: TagType[]
  created_at: string
  first_response_at: string | null
  team_id: number | null
  team: { id: number; name: string; emoji: string | null } | null
  // WhatsApp-specific fields
  whatsapp_last_inbound_at: string | null
  whatsapp_configuration: WhatsAppConfiguration | null
}

export interface Attachment {
  id: number
  signed_id: string
  filename: string
  content_type: string
  byte_size: number
  url: string
  is_video?: boolean
  is_image?: boolean
}

export interface PendingAttachment {
  id: string
  file: File
  previewUrl: string | null
}

// Simplified author type for message authors (subset of EndUser/Agent)
export interface MessageAuthor {
  id: number
  name: string
  email?: string | null
  avatar_url?: string | null
}

export interface Message {
  id: number
  body: string
  channel: 'chat' | 'email' | 'whatsapp'
  author_type: 'Agent' | 'EndUser' | null
  author: MessageAuthor | { id: null; name: string }
  created_at: string
  read_at: string | null
  delivered_at: string | null
  deleted_at: string | null
  edited_at: string | null
  attachments: Attachment[]
  sender_page_url: string | null
  sender_browser: string | null
  sender_os: string | null
  system_message_type?: 'away_notice' | 'email_capture' | 'rating_request' | null
  metadata?: {
    email_captured?: boolean
    email?: string
    rated?: boolean
    rating?: number
    agent_id?: number
    agent_name?: string
    has_comment?: boolean
  } | null
  // WhatsApp-specific fields
  whatsapp_status?: 'pending' | 'sent' | 'delivered' | 'read' | 'failed' | null
  whatsapp_error_message?: string | null
  // Reply-to
  replied_to?: {
    id: number
    body: string | null
    author_type: 'Agent' | 'EndUser' | null
    author_name: string
    attachments_count: number
    deleted: boolean
    image_url?: string | null
  } | null
  // Optimistic sending state
  _optimistic?: boolean
  _sendFailed?: boolean
  _tempId?: string
  _files?: File[]
}

export interface InternalNote {
  id: number
  body: string
  agent: Agent
  attachments?: Attachment[]
  created_at: string
  // Optimistic sending state
  _optimistic?: boolean
  _sendFailed?: boolean
  _tempId?: string
  _files?: File[]
}

export interface ComposeUser {
  id: number
  name: string | null
  email: string | null
  phone: string | null
  whatsappWaId: string | null
  companyName?: string | null
}

export interface WhatsAppTemplate {
  id: number
  name: string
  language: string
  category: 'UTILITY' | 'AUTHENTICATION'
  body_text: string
  header_text?: string
  variables: Array<{
    index: number
    type: 'text' | 'currency' | 'date_time'
    component: 'header' | 'body' | 'button'
    example?: string
  }>
}

export interface WhatsAppConfiguration {
  id: number
  name: string | null
  displayPhoneNumber: string | null
  isDefault: boolean
}

export interface InboxProps {
  conversations: ConversationListItem[]
  currentAgent: Agent
  agents: Agent[]
  filter: string
  statusFilter: string
  teamFilter: string | null
  channelFilter: string | null
  stats: {
    all: number
    assigned_to_me: number
    unassigned: number
    open: number
    closed: number
    snoozed: number
  }
  teams: Team[]
  selectedConversation: ConversationDetail | null
  selectedConversationId: number | null
  messages: Message[]
  internalNotes: InternalNote[]
  macros: Macro[]
  hasMore: boolean
  currentPage: number
  whatsappTemplates: WhatsAppTemplate[]
  whatsappConfigurations: WhatsAppConfiguration[]
}

export type TimelineItem =
  | { type: 'message'; data: Message; created_at: string }
  | { type: 'note'; data: InternalNote; created_at: string }

export type AssignmentFilterType = 'all' | 'assigned_to_me' | 'unassigned'
export type StatusFilterType = 'open' | 'closed' | 'snoozed'
export type ChannelFilterType = 'whatsapp' | 'chat' | null

export const DEFAULT_ASSIGNMENT_FILTER: AssignmentFilterType = 'all'
export const DEFAULT_STATUS_FILTER: StatusFilterType = 'open'

// Props reloaded when selecting a conversation. Static props (stats, agents,
// teams, macros, WhatsApp templates) are skipped — the server only runs the
// queries for these props, cutting both response time and payload size.
export const CONVERSATION_SELECT_PARTIAL_PROPS = [
  'conversations',
  'hasMore',
  'currentPage',
  'selectedConversation',
  'selectedConversationId',
  'messages',
  'internalNotes',
]
